// Staff Live Chat inbox — session list + thread, AI takeover, config/training.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, Send, Bot, BotOff, XCircle, Settings2, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLiveChatSocket } from '../hooks/useLiveChatSocket';
import { playReplyChime } from '../lib/replyChime';
import { useSearchParams } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MANAGER_ROLES = new Set(['master_admin', 'domain_admin', 'domain_supervisor', 'manager', 'supervisor']);

const ago = (iso) => {
  if (!iso) return '—';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
};

const ConfigPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    axios.get(`${API}/livechat/config`).then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  if (!cfg) return <p className="text-sm text-slate-400 p-4">Loading config…</p>;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/livechat/config`, cfg);
      toast.success('Live chat settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const field = 'w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:border-teal-500';
  return (
    <div className="space-y-3 p-4" data-testid="livechat-config-panel">
      <label className="flex items-center gap-2 text-sm font-medium">
        <input type="checkbox" checked={cfg.enabled} data-testid="livechat-cfg-enabled"
          onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} />
        Chat widget enabled for this domain
      </label>
      {user?.role === 'master_admin' && (
        <label className="flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={cfg.public_site} data-testid="livechat-cfg-public-site"
            onChange={(e) => setCfg({ ...cfg, public_site: e.target.checked })} />
          This domain powers the public voicefabric.com.au widget
        </label>
      )}
      <div>
        <p className="text-xs font-semibold text-slate-500 mb-1">Widget title</p>
        <input className={field} value={cfg.title} data-testid="livechat-cfg-title"
          onChange={(e) => setCfg({ ...cfg, title: e.target.value })} />
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-500 mb-1">Greeting</p>
        <input className={field} value={cfg.greeting} data-testid="livechat-cfg-greeting"
          onChange={(e) => setCfg({ ...cfg, greeting: e.target.value })} placeholder="Hi! Ask us anything…" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <p className="text-xs font-semibold text-slate-500 mb-1">Bubble colour</p>
          <div className="flex items-center gap-2">
            <input type="color" value={cfg.accent_color || '#14b8a6'} data-testid="livechat-cfg-accent"
              onChange={(e) => setCfg({ ...cfg, accent_color: e.target.value })}
              className="h-9 w-12 rounded border border-slate-200 cursor-pointer" />
            <span className="text-xs font-mono text-slate-500">{cfg.accent_color || '#14b8a6'}</span>
          </div>
        </div>
        <div className="md:col-span-2">
          <p className="text-xs font-semibold text-slate-500 mb-1">Logo URL (shown in the widget header)</p>
          <input className={field} value={cfg.logo_url || ''} data-testid="livechat-cfg-logo"
            onChange={(e) => setCfg({ ...cfg, logo_url: e.target.value })} placeholder="https://…/logo.png" />
        </div>
      </div>
      <div className="rounded-lg border border-slate-100 p-3" data-testid="livechat-cfg-hours">
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
          <input type="checkbox" checked={!!cfg.office_hours?.enabled} data-testid="livechat-cfg-hours-enabled"
            onChange={(e) => setCfg({ ...cfg, office_hours: { ...cfg.office_hours, enabled: e.target.checked } })} />
          Office hours — outside them the widget says “leave a message” instead of promising instant replies
        </label>
        {cfg.office_hours?.enabled && (
          <div className="flex flex-wrap items-end gap-3 mt-2">
            <label className="text-[11px] font-semibold text-slate-500">From
              <input type="time" value={cfg.office_hours?.start || '08:00'} data-testid="livechat-cfg-hours-start"
                onChange={(e) => setCfg({ ...cfg, office_hours: { ...cfg.office_hours, start: e.target.value } })}
                className="block mt-1 text-sm rounded-lg border border-slate-200 px-2 py-1.5" />
            </label>
            <label className="text-[11px] font-semibold text-slate-500">To
              <input type="time" value={cfg.office_hours?.end || '18:00'} data-testid="livechat-cfg-hours-end"
                onChange={(e) => setCfg({ ...cfg, office_hours: { ...cfg.office_hours, end: e.target.value } })}
                className="block mt-1 text-sm rounded-lg border border-slate-200 px-2 py-1.5" />
            </label>
            <label className="text-[11px] font-semibold text-slate-500">Timezone
              <input value={cfg.office_hours?.tz || 'Australia/Brisbane'} data-testid="livechat-cfg-hours-tz"
                onChange={(e) => setCfg({ ...cfg, office_hours: { ...cfg.office_hours, tz: e.target.value } })}
                className="block mt-1 text-sm rounded-lg border border-slate-200 px-2 py-1.5 w-44" />
            </label>
            <div className="flex gap-1">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => {
                const days = cfg.office_hours?.days || [0, 1, 2, 3, 4];
                const on = days.includes(i);
                return (
                  <button key={d} data-testid={`livechat-cfg-day-${d}`}
                    onClick={() => setCfg({ ...cfg, office_hours: { ...cfg.office_hours, days: on ? days.filter((x) => x !== i) : [...days, i] } })}
                    className={`text-[11px] font-bold rounded-md px-2 py-1.5 ${on ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-400'}`}>{d}</button>
                );
              })}
            </div>
            <label className="text-[11px] font-semibold text-slate-500 flex-1 min-w-[220px]">After-hours message
              <input value={cfg.offline_note || ''} data-testid="livechat-cfg-offline-note"
                onChange={(e) => setCfg({ ...cfg, offline_note: e.target.value })}
                placeholder="We're offline — leave a message and we'll reply first thing."
                className="block w-full mt-1 text-sm rounded-lg border border-slate-200 px-2 py-1.5" />
            </label>
          </div>
        )}
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-500 mb-1">AI instructions — tone, rules, what to push</p>
        <textarea className={field} rows={4} value={cfg.ai_instructions} data-testid="livechat-cfg-instructions"
          onChange={(e) => setCfg({ ...cfg, ai_instructions: e.target.value })}
          placeholder="e.g. Be friendly and brief. Always offer a free call audit. Never quote prices." />
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-500 mb-1">AI knowledge — paste FAQs, product info, pricing policy (the AI answers ONLY from this)</p>
        <textarea className={field} rows={8} value={cfg.ai_knowledge} data-testid="livechat-cfg-knowledge"
          onChange={(e) => setCfg({ ...cfg, ai_knowledge: e.target.value })}
          placeholder={'Q: What is VoiceFabric?\nA: AI-powered call grading and coaching…'} />
      </div>
      <button onClick={save} disabled={saving} data-testid="livechat-cfg-save"
        className="rounded-lg bg-teal-600 hover:bg-teal-500 text-white text-sm font-bold px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60">
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save settings
      </button>
      <div className="pt-3 mt-3 border-t border-slate-100" data-testid="livechat-embed-block">
        <p className="text-xs font-semibold text-slate-500 mb-1">Embed on any website — paste this one line before &lt;/body&gt;</p>
        <div className="flex gap-2">
          <code className="flex-1 text-[11px] bg-slate-900 text-teal-300 rounded-lg px-3 py-2 overflow-x-auto whitespace-nowrap" data-testid="livechat-embed-code">
            {`<script src="${process.env.REACT_APP_BACKEND_URL}/api/livechat/widget.js?domain=${cfg.domain_id}" async></script>`}
          </code>
          <button data-testid="livechat-embed-copy"
            onClick={() => { navigator.clipboard.writeText(`<script src="${process.env.REACT_APP_BACKEND_URL}/api/livechat/widget.js?domain=${cfg.domain_id}" async></script>`); toast.success('Embed code copied'); }}
            className="text-xs font-semibold rounded-lg border border-slate-200 px-3 text-slate-600 hover:bg-slate-50">Copy</button>
        </div>
        <p className="text-[11px] text-slate-400 mt-1">Same widget, this domain's branding, AI training and inbox. Works on any site — WordPress, Wix, custom.</p>
      </div>
      <CannedEditor />
    </div>
  );
};

const CannedEditor = () => {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/livechat/canned`).then((r) => setItems(r.data.canned || [])).catch(() => setItems([]));
  }, []);
  if (!items) return null;
  const save = async (next) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/livechat/canned`, { canned: next });
      setItems(r.data.canned);
      toast.success('Canned replies saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="pt-3 mt-3 border-t border-slate-100" data-testid="livechat-canned-editor">
      <p className="text-xs font-semibold text-slate-500 mb-2">Canned replies — one-tap answers for staff</p>
      {items.map((it, i) => (
        <div key={it.id || i} className="flex gap-2 mb-2">
          <input className="w-36 text-xs rounded-lg border border-slate-200 px-2 py-1.5" value={it.title}
            placeholder="Title" data-testid={`livechat-canned-title-${i}`}
            onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
          <input className="flex-1 text-xs rounded-lg border border-slate-200 px-2 py-1.5" value={it.text}
            placeholder="Reply text" data-testid={`livechat-canned-text-${i}`}
            onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
          <button onClick={() => save(items.filter((_, j) => j !== i))} data-testid={`livechat-canned-del-${i}`}
            className="text-red-500 hover:text-red-700 text-xs font-bold px-1">✕</button>
        </div>
      ))}
      <div className="flex gap-2">
        <button onClick={() => setItems([...items, { title: '', text: '' }])} data-testid="livechat-canned-add"
          className="text-xs font-semibold rounded-lg border border-slate-200 px-2.5 py-1.5 text-slate-600 hover:bg-slate-50">+ Add reply</button>
        <button onClick={() => save(items)} disabled={saving} data-testid="livechat-canned-save"
          className="text-xs font-bold rounded-lg bg-teal-600 hover:bg-teal-500 text-white px-3 py-1.5 disabled:opacity-60">Save canned replies</button>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white shadow-sm px-4 py-3" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
    <p className="text-xl font-bold text-slate-800">{value}</p>
  </div>
);

const AnalyticsStrip = () => {
  const [a, setA] = useState(null);
  useEffect(() => {
    axios.get(`${API}/livechat/analytics`).then((r) => setA(r.data)).catch(() => {});
  }, []);
  if (!a) return null;
  const thisWeek = a.weekly?.length ? a.weekly[a.weekly.length - 1].chats : 0;
  const fmt = (s) => (s == null ? '—' : s < 60 ? `${Math.round(s)}s` : `${Math.round(s / 60)}m`);
  const max = Math.max(1, ...(a.weekly || []).map((w) => w.chats));
  return (
    <div className="mb-5" data-testid="livechat-analytics">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Chats this week" value={thisWeek} testId="livechat-stat-week" />
        <StatCard label="AI resolved" value={a.ai_resolved} testId="livechat-stat-ai" />
        <StatCard label="Human handled" value={a.human_handled} testId="livechat-stat-human" />
        <StatCard label="Avg AI reply" value={fmt(a.avg_ai_response_secs)} testId="livechat-stat-ai-speed" />
        <StatCard label="Avg staff reply" value={fmt(a.avg_staff_response_secs)} testId="livechat-stat-staff-speed" />
      </div>
      {a.weekly?.length > 1 && (
        <div className="flex items-end gap-1 h-10 mt-2 px-1" data-testid="livechat-weekly-bars" title="Chats per week">
          {a.weekly.map((w) => (
            <div key={w.week} className="flex-1 bg-teal-500/70 rounded-t" style={{ height: `${(w.chats / max) * 100}%`, minHeight: 3 }} title={`${w.week}: ${w.chats}`} />
          ))}
        </div>
      )}
    </div>
  );
};

const LeadsPanel = () => {
  const [leads, setLeads] = useState([]);
  const load = useCallback(() => {
    axios.get(`${API}/livechat/leads`).then((r) => setLeads(r.data.leads || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => {
    try {
      await axios.post(`${API}/livechat/leads/${id}/status`, { status });
      toast.success('Lead updated');
      load();
    } catch { toast.error('Update failed'); }
  };
  const badge = { new: 'bg-teal-100 text-teal-700', contacted: 'bg-amber-100 text-amber-700', converted: 'bg-emerald-100 text-emerald-700', dismissed: 'bg-slate-100 text-slate-500' };
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="livechat-leads-panel">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[11px] uppercase text-slate-400 border-b border-slate-100">
          <th className="px-4 py-2">Visitor</th><th className="px-4 py-2">Contact</th><th className="px-4 py-2">Last chat</th><th className="px-4 py-2">Status</th>
        </tr></thead>
        <tbody>
          {leads.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-slate-400 text-sm">No chat leads yet.</td></tr>}
          {leads.map((l) => (
            <tr key={l.id} className="border-b border-slate-50" data-testid={`livechat-lead-${l.id}`}>
              <td className="px-4 py-2.5 font-medium text-slate-800">{l.name}</td>
              <td className="px-4 py-2.5 text-slate-500">{l.contact}</td>
              <td className="px-4 py-2.5 text-slate-400 text-xs">{ago(l.last_chat_at)}</td>
              <td className="px-4 py-2.5">
                <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)}
                  data-testid={`livechat-lead-status-${l.id}`}
                  className={`text-xs font-semibold rounded-full px-2 py-1 border-0 ${badge[l.status] || badge.new}`}>
                  {['new', 'contacted', 'converted', 'dismissed'].map((s2) => <option key={s2} value={s2}>{s2}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function LiveChatPage() {
  const { user } = useAuth();
  const isManager = MANAGER_ROLES.has(user?.role);
  const [tab, setTab] = useState('open');
  const [view, setView] = useState('chats');
  const [canned, setCanned] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [draft, setDraft] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [searchParams] = useSearchParams();
  const bottomRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = active;

  const loadSessions = useCallback(() => {
    axios.get(`${API}/livechat/sessions`, { params: { status: tab } })
      .then((r) => setSessions(r.data.sessions || [])).catch(() => {});
  }, [tab]);

  const loadThread = useCallback((sid) => {
    axios.get(`${API}/livechat/sessions/${sid}/thread`)
      .then((r) => setThread(r.data)).catch(() => setThread(null));
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => {
    axios.get(`${API}/livechat/canned`).then((r) => setCanned(r.data.canned || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const s = searchParams.get('session');
    if (s) { setActive(s); loadThread(s); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread?.messages?.length]);

  useLiveChatSocket(useCallback((evt) => {
    playReplyChime();
    toast.info(`Live chat — ${evt.visitor_name}: “${(evt.preview || '').slice(0, 60)}”`);
    loadSessions();
    if (activeRef.current === evt.session_id) loadThread(evt.session_id);
  }, [loadSessions, loadThread]));

  const open = (sid) => { setActive(sid); loadThread(sid); loadSessions(); };

  const reply = async () => {
    const content = draft.trim();
    if (!content || !active) return;
    setDraft('');
    try {
      await axios.post(`${API}/livechat/sessions/${active}/reply`, { content });
      loadThread(active);
    } catch (e) { toast.error(e.response?.data?.detail || 'Reply failed'); }
  };

  const toggleAi = async (on) => {
    try {
      await axios.post(`${API}/livechat/sessions/${active}/ai`, { active: on });
      toast.success(on ? 'AI is answering again' : 'AI paused — you have the chat');
      loadThread(active);
    } catch { toast.error('Failed'); }
  };

  const closeChat = async () => {
    try {
      await axios.post(`${API}/livechat/sessions/${active}/close`);
      toast.success('Chat closed — transcript emailed if contact was left');
      setActive(null); setThread(null); loadSessions();
    } catch { toast.error('Failed to close'); }
  };

  const s = thread?.session;
  return (
    <div data-testid="livechat-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-teal-600" /> Live Chat
          </h1>
          <p className="text-sm text-slate-500">Website visitors chatting with your team — AI answers first, you take over anytime.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadSessions} data-testid="livechat-refresh" className="text-slate-500 hover:text-slate-800 p-2"><RefreshCw className="w-4 h-4" /></button>
          {isManager && (
            <button onClick={() => setShowConfig((v) => !v)} data-testid="livechat-config-toggle"
              className={`text-sm font-semibold rounded-lg px-3 py-2 inline-flex items-center gap-1.5 border ${showConfig ? 'bg-teal-600 text-white border-teal-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              <Settings2 className="w-4 h-4" /> Widget & AI training
            </button>
          )}
        </div>
      </div>

      {showConfig && <div className="mb-6 rounded-xl border border-slate-200 bg-white shadow-sm"><ConfigPanel /></div>}

      <AnalyticsStrip />

      <div className="flex gap-1 mb-3">
        {['chats', 'leads'].map((v) => (
          <button key={v} onClick={() => setView(v)} data-testid={`livechat-view-${v}`}
            className={`text-sm font-semibold rounded-lg px-3 py-1.5 capitalize ${view === v ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>{v}</button>
        ))}
      </div>

      {view === 'leads' ? <LeadsPanel /> : (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="flex border-b border-slate-100">
            {['open', 'closed'].map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`livechat-tab-${t}`}
                className={`flex-1 py-2 text-sm font-semibold capitalize ${tab === t ? 'text-teal-700 border-b-2 border-teal-600' : 'text-slate-400'}`}>{t}</button>
            ))}
          </div>
          <div className="max-h-[540px] overflow-y-auto divide-y divide-slate-100" data-testid="livechat-session-list">
            {sessions.length === 0 && <p className="p-4 text-sm text-slate-400">No {tab} chats.</p>}
            {sessions.map((x) => (
              <button key={x.id} onClick={() => open(x.id)} data-testid={`livechat-session-${x.id}`}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${active === x.id ? 'bg-teal-50' : ''}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold truncate">{x.visitor_name}
                    {x.unread && <span className="ml-2 inline-block w-2 h-2 rounded-full bg-teal-500 align-middle" data-testid="livechat-unread-dot" />}
                  </p>
                  <span className="text-[10px] text-slate-400 shrink-0">{ago(x.last_message_at)}</span>
                </div>
                <p className="text-xs text-slate-500 truncate">{x.last_role === 'ai' ? 'AI: ' : ''}{x.last_preview}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col min-h-[540px]">
          {!thread ? (
            <p className="m-auto text-sm text-slate-400" data-testid="livechat-no-selection">Select a chat to view the conversation.</p>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-semibold text-sm" data-testid="livechat-thread-visitor">{s.visitor_name} <span className="text-slate-400 font-normal">· {s.visitor_contact}</span></p>
                  <p className="text-[11px] text-slate-400">{s.ai_active ? 'AI is answering' : `Taken over${s.taken_over_by_name ? ` by ${s.taken_over_by_name}` : ''}`}</p>
                </div>
                {s.status === 'open' && (
                  <div className="flex gap-2">
                    <button onClick={() => toggleAi(!s.ai_active)} data-testid="livechat-ai-toggle"
                      className="text-xs font-semibold rounded-lg border border-slate-200 px-2.5 py-1.5 inline-flex items-center gap-1 text-slate-600 hover:bg-slate-50">
                      {s.ai_active ? <><BotOff className="w-3.5 h-3.5" /> Pause AI</> : <><Bot className="w-3.5 h-3.5" /> Hand back to AI</>}
                    </button>
                    <button onClick={closeChat} data-testid="livechat-close-session"
                      className="text-xs font-semibold rounded-lg border border-red-200 text-red-600 px-2.5 py-1.5 inline-flex items-center gap-1 hover:bg-red-50">
                      <XCircle className="w-3.5 h-3.5" /> Close
                    </button>
                  </div>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[400px]" data-testid="livechat-thread-messages">
                {thread.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.role === 'visitor' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-[13px] ${
                      m.role === 'visitor' ? 'bg-slate-100 text-slate-800'
                        : m.role === 'ai' ? 'bg-cyan-50 border border-cyan-100 text-slate-700'
                          : 'bg-teal-600 text-white'}`}>
                      <p className="text-[10px] font-bold opacity-60 mb-0.5">{m.sender_name || m.role}</p>
                      {m.content}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              {s.status === 'open' && (
                <div className="p-3 border-t border-slate-100">
                  {canned.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="livechat-canned-chips">
                      {canned.map((c) => (
                        <button key={c.id} onClick={() => setDraft(c.text)} title={c.text}
                          data-testid={`livechat-canned-chip-${c.id}`}
                          className="text-[11px] font-semibold rounded-full border border-teal-200 text-teal-700 bg-teal-50 hover:bg-teal-100 px-2.5 py-1">
                          {c.title}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input value={draft} onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && reply()}
                      placeholder={`Reply to ${s.visitor_name}… (pauses the AI)`} data-testid="livechat-reply-input"
                      className="flex-1 text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:border-teal-500" />
                    <button onClick={reply} data-testid="livechat-reply-send"
                      className="rounded-lg bg-teal-600 hover:bg-teal-500 text-white px-3 inline-flex items-center"><Send className="w-4 h-4" /></button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
